import React, { Dispatch, SetStateAction, useState } from "react"
import * as graphql from "lib/types/generated/graphql-types"
import { OrderOperationSearchHit } from "lib/types/generated/graphql-types"
import { Container, Providers, ProviderWrapper, Title } from "./FailedRefund.styled"
import Totals from "./Cart/Totals"
import { useMutation, useQuery } from "@apollo/client"
import GET_REFUND from "../../../../graphql/queries/order/GetRefund"
import SecondaryButton from "../../../Ui/Buttons/SecondaryButton"
import { Tooltip } from "../../../Ui/Tooltip"
import CopyButton from "../../../Ui/CopyButton"
import {
  ActionWrapper,
  GiftCardInputWrapper,
  LabelWrapper,
  StyledActionDropdownButton
} from "./Cart/RefundAction.styled"
import ActionInfoBox from "../../../OperationsDashboard/OperationActions/ActionInfoBox"
import { ActionDropdownButton } from "../../../Ui/Buttons/ActionDropdownButton"
import Input from "../../../Ui/Form/Input"
import alertActions from "lib/store/services/Alert/AlertSlice"
import RESTART_REFUND from "../../../../graphql/mutations/order/refunds/RestartRefund"
import { useDispatch } from "react-redux"
import { ReactComponent as GiftCardIcon } from "../../../../images/icons/gift.svg"
import { ReactComponent as BonusIcon } from "../../../../images/icons/hand-holding-dollar.svg"
import { ReactComponent as PaymentIcon } from "../../../../images/icons/credit-card.svg"
import { ReactComponent as TaxIcon } from "../../../../images/icons/credit-card.svg"
import ErrorMessages from "../ErrorMessages"
import { ButtonWrapper } from "../ReleaseOrder/FailedRelease.styled"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"

type Providers = {
  bonus: boolean
  giftCard: boolean
  payment: boolean
  tax: boolean
}

type Props = {
  failedRefund: graphql.OrderRefund
  currencyCode: string
  orderId: string
  orderHistory: graphql.OrderHistoryLogEntry[]
  refetch?: () => void
  setFailedOperations?: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  isOperationBlocked?: boolean
  blockedReason?: string
}

enum FAILURE {
  BONUS = "REFUND_BONUS_FAILURE",
  GIFT_CARD = "REFUND_GIFT_CARD_FAILURE",
  PAYMENT = "REFUND_PAYMENT_FAILURE",
  TAX = "REFUND_TAX_FAILURE"
}

const FailedRefund = ({
  failedRefund,
  currencyCode,
  refetch,
  orderHistory,
  setFailedOperations,
  isOperationBlocked = false,
  blockedReason = ""
}: Props) => {
  const [failedProviders, setFailedProviders] = useState<Providers | undefined>()
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentRefund>({
    actionType: graphql.InputActionType.Auto
  })
  const [taxAction, setTaxAction] = useState<graphql.InputActionTax>({
    actionType: graphql.InputActionType.Auto
  })

  const dispatch = useDispatch()

  const [restartRefund, { loading }] = useMutation(RESTART_REFUND, {
    onCompleted: () => {
      refetch && refetch()
      if (setFailedOperations) {
        setFailedOperations((prev) => prev.filter((hit) => hit.id !== failedRefund.id))
        return
      }
    },
    onError: (error) => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error",
          message: error.message
        })
      )
    }
  })

  const restartOrderRefund = () => {
    restartRefund({
      variables: {
        refundId: failedRefund.id,
        input: {
          ...(failedProviders?.bonus && { bonus: bonusAction }),
          ...(failedProviders?.giftCard && { giftCard: giftCardAction }),
          ...(failedProviders?.payment && { payment: paymentAction }),
          ...(failedProviders?.tax && { tax: taxAction })
        }
      }
    })
  }

  const updateGiftCardActionCards = (id: string, value: string) => {
    const idExists = giftCardAction?.giftCards?.find((giftCard) => giftCard.giftCardId === id)
    if (idExists) {
      return setGiftCardAction({
        ...giftCardAction,
        giftCards: giftCardAction?.giftCards?.map((giftCard) =>
          giftCard.giftCardId === id
            ? {
                giftCardId: id,
                transactionId: value
              }
            : giftCard
        )
      })
    }
    return setGiftCardAction({
      ...giftCardAction,
      giftCards: [...(giftCardAction.giftCards ?? []), { giftCardId: id, transactionId: value }]
    })
  }

  const updatePaymentCaptureReference = (reference: string) => {
    setPaymentAction({ ...paymentAction, refundReference: reference })
  }

  useQuery(GET_REFUND, {
    variables: { refundId: failedRefund.id },
    onCompleted: (data) => {
      const refundDetails = data?.Order?.refund
      setFailedProviders({
        bonus: refundDetails?.bonusProvider?.status?.current === FAILURE.BONUS,
        giftCard: refundDetails?.giftCardProvider?.status?.current === FAILURE.GIFT_CARD,
        payment: refundDetails?.paymentProvider?.status?.current === FAILURE.PAYMENT,
        tax: refundDetails?.taxProvider?.status?.current === FAILURE.TAX,
      })
    }
  })
  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  return (
    <>
      <Container>
        <Title>
          <div>
            <h2>Failed Refund</h2>
          </div>
        </Title>
        id: {failedRefund.id} <CopyButton string={failedRefund.id} />
        {failedRefund?.paymentProvider && (
          <div>
            <b>Payment</b> - {getPaymentProviderDisplayName(failedRefund?.paymentProvider?.providerName)}
          </div>
        )}
        {failedRefund?.giftCardProvider && (
          <div>
            <b>Gift card</b> - {failedRefund?.giftCardProvider?.providerName}
          </div>
        )}
        {failedRefund?.bonus && (
          <div>
            <b>Bonus</b> - {failedRefund?.bonusProvider?.providerName}
          </div>
        )}
        {failedRefund?.taxProvider && (
          <div>
            <b>Tax</b> - {failedRefund?.taxProvider?.providerName}
          </div>
        )}
        <ProviderWrapper>
          <h4>Failed providers:</h4>
        </ProviderWrapper>
        <>
          <ActionInfoBox />
          <Providers>
            {failedProviders?.giftCard && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <GiftCardIcon />
                    <b>GiftCard - {failedRefund.giftCardProvider?.providerName}</b>
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(giftCardAction.actionType)}>
                    <li
                      onClick={() =>
                        setGiftCardAction({ actionType: graphql.InputActionType.Auto })
                      }
                    >
                      Auto
                    </li>
                    <li
                      onClick={() =>
                        setGiftCardAction({ actionType: graphql.InputActionType.Manual })
                      }
                    >
                      Manual
                    </li>
                    <li
                      onClick={() =>
                        setGiftCardAction({ actionType: graphql.InputActionType.Skip })
                      }
                    >
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
                {giftCardAction.actionType === graphql.InputActionType.Manual && (
                  <>
                    {failedRefund?.giftCards?.map((giftCard) => {
                      return (
                        <GiftCardInputWrapper key={giftCard.giftCardId}>
                          <p>{giftCard.giftCardId}</p>
                          <Input
                            removePaddingBottom={true}
                            name="transactionId"
                            placeholder="Transaction id"
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              updateGiftCardActionCards(giftCard.giftCardId, e.currentTarget.value)
                            }
                          />
                        </GiftCardInputWrapper>
                      )
                    })}
                  </>
                )}
              </div>
            )}
            {failedProviders?.bonus && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <BonusIcon />
                    <b>Bonus - {failedRefund.bonusProvider?.providerName}</b>{" "}
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(bonusAction.actionType)}>
                    <li
                      onClick={() => setBonusAction({ actionType: graphql.InputActionType.Auto })}
                    >
                      Auto
                    </li>
                    <li
                      onClick={() => setBonusAction({ actionType: graphql.InputActionType.Manual })}
                    >
                      Manual
                    </li>
                    <li
                      onClick={() => setBonusAction({ actionType: graphql.InputActionType.Skip })}
                    >
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
            {failedProviders?.tax && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <TaxIcon />
                    <b>Tax - {failedRefund.taxProvider?.providerName}</b>{" "}
                  </LabelWrapper>
                  <ActionDropdownButton title={getLabel(taxAction.actionType)}>
                    <li
                      onClick={() => setTaxAction({ actionType: graphql.InputActionType.Auto })}
                    >
                      Auto
                    </li>
                    <li
                      onClick={() => setTaxAction({ actionType: graphql.InputActionType.Manual })}
                    >
                      Manual
                    </li>
                    <li
                      onClick={() => setTaxAction({ actionType: graphql.InputActionType.Skip })}
                    >
                      Skip
                    </li>
                  </ActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
            {failedProviders?.payment && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <PaymentIcon />
                    <b>Payment - {failedRefund.paymentProvider?.providerName}</b>
                  </LabelWrapper>
                  <StyledActionDropdownButton title={getLabel(paymentAction.actionType)}>
                    <li
                      onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Auto })}
                    >
                      Auto
                    </li>
                    <li
                      onClick={() =>
                        setPaymentAction({ actionType: graphql.InputActionType.Manual })
                      }
                    >
                      Manual
                    </li>
                    <li
                      onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Skip })}
                    >
                      Skip
                    </li>
                  </StyledActionDropdownButton>
                </ActionWrapper>
                {paymentAction.actionType === graphql.InputActionType.Manual && (
                  <Input
                    name="captureReference"
                    label="Capture reference"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updatePaymentCaptureReference(e.currentTarget.value)
                    }
                  />
                )}
              </div>
            )}
          </Providers>
        </>
        <Totals
          totals={failedRefund.totals}
          currencyCode={currencyCode}
          refundFee={failedRefund?.fee}
        />
        <ErrorMessages
          orderHistory={orderHistory}
          operationId={failedRefund.id}
          operation={"REFUND"}
        />
        <ButtonWrapper>
          <SecondaryButton
            handleClick={restartOrderRefund}
            loading={loading}
            disabled={isOperationBlocked}
            data-tooltip-id="blocked-restart-tooltip"
            data-tooltip-content={isOperationBlocked ? blockedReason || "Blocked by a custom state" : undefined}
          >
            Restart refund
          </SecondaryButton>
          {isOperationBlocked && <Tooltip id="blocked-restart-tooltip" />}
        </ButtonWrapper>
      </Container>
    </>
  )
}

export default FailedRefund
